import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Music as MusicIcon } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from './ui/carousel';

const Music = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (inView) {
      setIsPlaying(true);
    }
  }, [inView]);

  // Multiple Spotify playlists - add your playlist IDs here
  const musicPlaylists = [
    {
      id: '4XQ9JAyS152dGJfQo531ns',
      title: 'My Favorites',
      type: 'playlist'
    },
    {
      id: '5AvA6TwpLNWYlY4o8g6Mkk',
      title: 'Chill Vibes',
      type: 'playlist'
    },
    {
      id: '1jLWm1f8vLxdLtrVvjBhOx',
      title: 'Top Hits',
      type: 'playlist'
    },
  ];

  // Function to get Spotify embed URL
  const getSpotifyEmbedUrl = (id, type = 'playlist') => {
    return `https://open.spotify.com/embed/${type}/${id}`;
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="music" className="py-20 md:py-32 bg-transparent relative overflow-hidden" ref={ref}>
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden -z-10">
        <motion.div
          className="absolute w-48 h-48 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-3xl"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: isPlaying ? [1, 1.2, 1] : 1,
            opacity: isPlaying ? [0.5, 0.7, 0.5] : 0.2,
          }}
          transition={{ duration: isPlaying ? 20 : 0, repeat: Infinity, ease: 'easeInOut' }}
          style={{ top: '10%', left: '5%' }}
        />
        <motion.div
          className="absolute w-64 h-64 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-3xl"
          animate={{
            x: [0, -60, 0],
            y: [0, 40, 0],
            scale: isPlaying ? [0.9, 1.1, 0.9] : 1,
            opacity: isPlaying ? [0.4, 0.6, 0.4] : 0.15,
          }}
          transition={{ duration: isPlaying ? 25 : 0, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          style={{ bottom: '10%', right: '5%' }}
        />
        <motion.div
          className="absolute w-56 h-56 bg-gradient-to-r from-lime-500 to-green-500 rounded-full blur-3xl"
          animate={{
            x: [0, 40, 0],
            y: [0, -50, 0],
            scale: isPlaying ? [1, 1.15, 1] : 1,
            opacity: isPlaying ? [0.45, 0.65, 0.45] : 0.18,
          }}
          transition={{ duration: isPlaying ? 30 : 0, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          style={{ top: '50%', right: '10%' }}
        />
        <motion.div
          className="absolute w-40 h-40 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full blur-3xl"
          animate={{
            x: [0, -50, 0],
            y: [0, 60, 0],
            scale: isPlaying ? [1, 1.1, 1] : 1,
            opacity: isPlaying ? [0.5, 0.7, 0.5] : 0.2,
          }}
          transition={{ duration: isPlaying ? 28 : 0, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
          style={{ bottom: '20%', left: '10%' }}
        />
      </div>
      
      <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="space-y-12"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="space-y-4">
            <div className="flex items-center gap-3">
              <MusicIcon className="w-8 h-8 text-green-500" />
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Music & Vibes
              </h2>
            </div>
            <p className="text-gray-300 text-lg max-w-2xl">
              Check out the music I'm currently loving. Here are some of my favorite playlists and artists on Spotify.
            </p>
          </motion.div>

          {/* Spotify Carousel */}
          <motion.div variants={itemVariants} className="w-full">
            <Carousel className="w-full">
              <CarouselContent className="-ml-2 md:-ml-4">
                {musicPlaylists.map((playlist) => (
                  <CarouselItem key={playlist.id} className="pl-2 md:pl-4 basis-full md:basis-1/2 lg:basis-1/2">
                    <div className="rounded-lg overflow-hidden bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 hover:border-green-500/30 transition-colors p-4 h-full">
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg font-semibold text-white mb-3 truncate">
                          {playlist.title}
                        </h3>
                        <div className="flex-1 flex justify-center overflow-hidden rounded">
                          <iframe
                            src={`${getSpotifyEmbedUrl(playlist.id, playlist.type)}?utm_source=generator`}
                            width="100%"
                            height="380"
                            frameBorder="0"
                            allowFullScreen
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                            className="rounded"
                            title={`Spotify ${playlist.title}`}
                          />
                        </div>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="absolute -left-12 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-600 text-black border-0 h-10 w-10" />
              <CarouselNext className="absolute -right-12 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-600 text-black border-0 h-10 w-10" />
            </Carousel>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Music;
